import { t } from "@lingui/macro";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AlertColor } from "@mui/material/Alert";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import Alerts from "@/client/components/alerts";
import ChipLabel from "@/client/components/custom/custom-chip";
import { useAppContext } from "@/client/hooks/context";
import { useUser } from "@/client/services/user";
import { AmountFormat } from "@/client/components/custom/common-format";

// ─── Helpers ──────────────────────────────────────────────────────────────
const formatDateTime = (dateString: string, timeString: string) => {
  if (!dateString || !timeString) return "--";
  try {
    const date = parseISO(dateString);
    const [hours, minutes] = timeString.split(":").map(Number);
    date.setHours(hours, minutes);
    // eslint-disable-next-line lingui/no-unlocalized-strings
    return format(date, "MMM dd hh:mm a");
  } catch (error) {
    console.error(t`Error formatting date/time:`, error);
    return "--";
  }
};

// ─── Styled Components ───────────────────────────────────────────────────
const FilterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.75, 2),
  backgroundColor: "#f7faff",
  borderRadius: "12px",
  border: "1px solid #dde8fb",
  marginBottom: theme.spacing(1.5),
}));

const filterSelectSx = {
  fontSize: "13px",
  bgcolor: "#fff",
  borderRadius: "8px",
  width: 140,
  minWidth: 140,
  maxWidth: 140,
  "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
};

// ── Matched to Assessments: rounded top, no bottom border ──
const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "12px 12px 0 0",
  border: "1px solid #eaf0fb",
  borderBottom: "none",
  boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
  maxHeight: "65vh",
  overflowY: "auto",
}));

const StyledTableHead = styled(TableHead)(() => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  "& .MuiTableCell-root": {
    backgroundColor: "#f7faff",
    fontWeight: 700,
    fontSize: "13px",
    color: "#374151",
    borderBottom: "2px solid #dde8fb",
    whiteSpace: "nowrap",
    padding: "10px 14px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "background-color 0.15s",
  "&:hover": { backgroundColor: "#f0f6ff" },
  "&:last-child .MuiTableCell-root": { borderBottom: "none" },
  "& .MuiTableCell-root": {
    fontSize: "13px",
    color: "#374151",
    borderBottom: "1px solid #eaf0fb",
    padding: "10px 14px",
  },
}));

// ─── Status Chip ──────────────────────────────────────────────────────────
const StatusChip = ({ status }: { status: string }) => {
  switch (status) {
    case "published":
      return <ChipLabel label={t`Published`} sx={{ color: "blue.main", backgroundColor: "blue.light", fontSize: "11px", p: "0px", height: "24px" }} />;
    case "ongoing":
      return <ChipLabel label={t`Ongoing`} sx={{ color: "orange.main", backgroundColor: "orange.light", fontSize: "11px", p: "0px", height: "24px" }} />;
    case "completed":
      return <ChipLabel label={t`Completed`} sx={{ color: "green.main", backgroundColor: "green.light", fontSize: "11px", p: "0px", height: "24px" }} />;
    case "cancelled":
      return <ChipLabel label={t`Cancelled`} sx={{ color: "red.main", backgroundColor: "red.light", fontSize: "11px", p: "0px", height: "24px" }} />;
    default:
      return <ChipLabel label={t`Published`} sx={{ color: "blue.main", backgroundColor: "blue.light", fontSize: "11px", p: "0px", height: "24px" }} />;
  }
};

// ─── Constants ────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

// ─── Main Component ───────────────────────────────────────────────────────
const WebinarPage = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [data, setData] = useState<any[]>([]);
  const { user } = useUser();
  const { loading, setLoading, reloading } = useAppContext();
  const userEmail = user?.email;
  const userName = user?.name || user?.email || "Student";

  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({ open: false, message: "", severity: "info" });

  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!userEmail) {
      setData([]);
      return;
    }
    const fetchData = () => {
      setLoading(true);
      try {
        const timer = setTimeout(async () => {
          try {
            const res = await axios.get("/api/integration/external-service/webinar", {
              params: { email: userEmail },
            });
            const payload = res.data;
            let result: any[] = [];
            if (Array.isArray(payload)) result = payload;
            else if (Array.isArray(payload?.result)) result = payload.result;
            else if (Array.isArray(payload?.data)) result = payload.data;

            result.sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            setData(result);
          } catch (error) {
            console.error(error);
          } finally {
            setLoading(false);
          }
        }, 1000);
        return () => clearTimeout(timer);
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, [reloading, setLoading, userEmail]);

  const handleCloseAlert = () => setAlert({ open: false, message: "", severity: "info" });

  const handleAttendWebinar = async (id: string) => {
    if (!userEmail) {
      setAlert({ open: true, message: t`Unable to identify your email`, severity: "error" });
      return;
    }
    try {
      const response = await axios.post(
        `/api/integration/external-service/webinar/${id}/join`,
        { email: userEmail, name: userName }
      );
      const payload = response.data?.result ?? response.data;
      if (payload?.joinUrl) {
        window.open(payload.joinUrl, "_blank");
        setAlert({ open: true, message: t`Opening webinar room...`, severity: "success" });
        return;
      }
      setAlert({ open: true, message: t`Join URL is not available`, severity: "error" });
    } catch (error: any) {
      setAlert({
        open: true,
        message: error.response?.data?.error || t`Failed to open webinar`,
        severity: "error",
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows = useMemo(
    () =>
      [...data]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map((item: any) => ({
          id: item._id,
          code: item.webinar_code,
          webinarName: item.webinar_name,
          category: item.webinar_category || "--",
          organizer: item.organizer?.name || "--",
          startDateTime: formatDateTime(item.start_date, item.start_time),
          duration: item.duration ? `${item.duration} min` : "--",
          fees: item.fees ?? 0,
          status: (item.currentStatus || item.status || "published").toLowerCase(),
        })),
    [data]
  );

  const categoryOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.category).filter((c) => c !== "--")))],
    [rows]
  );
  const statusOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.status)))],
    [rows]
  );

  const filteredRows = useMemo(() => {
    const query = search.toLowerCase().trim();
    return rows.filter((row) => {
      if (
        query &&
        !row.webinarName.toLowerCase().includes(query) &&
        !row.code.toLowerCase().includes(query)
      )
        return false;
      if (filterCategory !== "All" && row.category !== filterCategory) return false;
      if (filterStatus !== "All" && row.status !== filterStatus) return false;
      return true;
    });
  }, [rows, search, filterCategory, filterStatus]);

  const hasActiveFilters = search !== "" || filterCategory !== "All" || filterStatus !== "All";

  const clearFilters = () => {
    setSearch("");
    setFilterCategory("All");
    setFilterStatus("All");
    setPage(0);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [search, filterCategory, filterStatus]);

  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  return (
    <Box sx={{ flexGrow: 1, px: 1 }}>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            fontSize: "15px",
            fontWeight: 700,
            color: "primary.main",
            mb: "-2px",
            userSelect: "none",
          }}
        >
          {t`Webinars`}
        </Box>
      </Box>

      {/* Filter Bar */}
      <FilterBar sx={{ flexWrap: "nowrap", alignItems: "center", gap: 1, py: 1.25 }}>
        <FilterListIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
        <Typography
          sx={{ fontSize: "12px", fontWeight: 600, color: "primary.main", whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {t`Filters`}
        </Typography>

        <TextField
          size="small"
          placeholder={t`Search name, code...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { fontSize: "12px", borderRadius: "8px", bgcolor: "#fff" },
          }}
          sx={{
            flex: "1 1 140px",
            minWidth: 120,
            maxWidth: 180,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "none !important", outline: "none !important" },
              "& input:focus": { outline: "none !important", boxShadow: "none !important" },
          }}
        />

        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Category`}</InputLabel>
          <Select
            label={t`Category`}
            value={filterCategory}
            onChange={(e: SelectChangeEvent) => setFilterCategory(e.target.value)}
            sx={{ ...filterSelectSx, width: "100%", minWidth: "unset", maxWidth: "unset", fontSize: "12px" }}
          >
            {categoryOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "12px" }}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Status`}</InputLabel>
          <Select
            label={t`Status`}
            value={filterStatus}
            onChange={(e: SelectChangeEvent) => setFilterStatus(e.target.value)}
            sx={{ ...filterSelectSx, width: "100%", minWidth: "unset", maxWidth: "unset", fontSize: "12px" }}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "12px", textTransform: "capitalize" }}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", flexShrink: 0 }}>
          {!loading && (
            <Typography sx={{ fontSize: "12px", color: "text.disabled", whiteSpace: "nowrap" }}>
              {hasActiveFilters
                ? `${filteredRows.length} of ${rows.length} webinars`
                : `${rows.length} webinars`}
            </Typography>
          )}
          {hasActiveFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clearFilters}
              onClick={clearFilters}
              sx={{
                fontSize: "11px",
                bgcolor: "#fff0f0",
                color: "#c62828",
                borderColor: "#ffcdd2",
                "& .MuiChip-deleteIcon": { color: "#c62828" },
              }}
              variant="outlined"
            />
          )}
        </Box>
      </FilterBar>

      {/* Table */}
      {loading ? (
        <Box
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 2 }}
        >
          <CircularProgress size={36} />
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {t`Loading webinars...`}
          </Typography>
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {hasActiveFilters
              ? t`No webinars match your filters. Try adjusting or clearing them.`
              : t`No webinars found.`}
          </Typography>
        </Box>
      ) : (
        // ── Matched to Assessments: Paper wrapper with overflow:hidden ──
        <Paper
          sx={{
            borderRadius: "12px",
            border: "1px solid #eaf0fb",
            boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <StyledTableContainer>
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 860 }}>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ width: "100px" }}>{t`Code`}</TableCell>
                  <TableCell sx={{ width: "150px" }}>{t`Webinar Name`}</TableCell>
                  <TableCell sx={{ width: "110px" }}>{t`Category`}</TableCell>
                  <TableCell sx={{ width: "120px" }}>{t`Organizer`}</TableCell>
                  <TableCell align="center" sx={{ width: "130px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
                      <CalendarTodayIcon sx={{ fontSize: 13 }} />
                      {t`Start Date & Time`}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "90px" }}>{t`Duration`}</TableCell>
                  <TableCell align="center" sx={{ width: "80px" }}>{t`Fees`}</TableCell>
                  <TableCell align="center" sx={{ width: "100px" }}>{t`Status`}</TableCell>
                  <TableCell align="center" sx={{ width: "110px" }}>{t`Actions`}</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {paginatedRows.map((row) => (
                  <StyledTableRow key={row.id}>
                    <TableCell sx={{ width: "100px", overflow: "hidden" }}>
                      <Chip
                        label={row.code}
                        size="small"
                        sx={{
                          fontSize: "11px",
                          bgcolor: "#f0f4ff",
                          color: "#1976d2",
                          fontWeight: 600,
                          maxWidth: "84px",
                          overflow: "hidden",
                        }}
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell sx={{ maxWidth: "180px", minWidth: "180px", width: "180px" }}>
                      <Tooltip title={row.webinarName} placement="top">
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: "primary.main",
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.webinarName}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ width: "110px", overflow: "hidden" }}>
                      <Chip
                        label={row.category}
                        size="small"
                        sx={{
                          fontSize: "11px",
                          width: "90px",
                          minWidth: "90px",
                          justifyContent: "center",
                          bgcolor: "#f0f4ff",
                          color: "#374151",
                        }}
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell sx={{ width: "120px", overflow: "hidden" }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.organizer}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ width: "130px", overflow: "hidden" }}>
                      <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                        {row.startDateTime}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ width: "90px", overflow: "hidden" }}>
                      <Typography sx={{ fontSize: "13px" }}>{row.duration}</Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ width: "80px", overflow: "hidden" }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: row.fees > 0 ? "#1976d2" : "text.secondary",
                        }}
                      >
                        {row.fees > 0 ? AmountFormat(row.fees) : t`Free`}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ width: "90px", overflow: "hidden" }}>
                      <StatusChip status={row.status} />
                    </TableCell>

                    <TableCell align="center" sx={{ width: "110px", overflow: "hidden" }}>
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75 }}>
                        {row.status === "ongoing" && (
                          <Tooltip title={t`Attend Webinar`}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              sx={{
                                textTransform: "none",
                                fontSize: "11px",
                                height: "26px",
                                width: "100px",
                                minWidth: "100px",
                              }}
                              onClick={() => handleAttendWebinar(row.id)}
                            >
                              {t`Attend`}
                              <ArrowForwardIcon sx={{ fontSize: 12, ml: 0.4 }} />
                            </Button>
                          </Tooltip>
                        )}
                        {row.status !== "ongoing" && (
                          <Button
                            disabled
                            variant="contained"
                            size="small"
                            sx={{
                              textTransform: "none",
                              fontSize: "11px",
                              height: "26px",
                              width: "100px",
                              minWidth: "100px",
                            }}
                          >
                            {row.status === "completed" ? t`Expired` : t`Not Available`}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* ── Pagination: matched to Assessments ── */}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid #eaf0fb",
              bgcolor: "#f7faff",
              fontSize: "12px",
              "& .MuiTablePagination-toolbar": { minHeight: "40px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12px",
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default WebinarPage;